/**
 * Team Design — shared SVG engine.
 *
 * Single source of truth for pattern-set SVG fetching, in-memory caching, and
 * 4-color theme substitution. Replaces the previously duplicated svgCache /
 * colorize / fetchSvg pairs that lived in both the swatch hover system and
 * the team-name overlay system, so a pattern fetched once is shared by all
 * consumers (swatches, team text, team logo).
 *
 * Exposes window.TeamDesignSvg — consumed by:
 *   - team-design.swatches.js
 *   - team-design.text-render.js
 *   - team-design.text-auto.js
 *   - team-design.logo.js
 *
 * Must load before any of the above.
 */
(function () {
  'use strict';

  // Local `shopify theme dev` serves the storefront from http://127.0.0.1:9292
  // and `window.mediaBaseUrl` is the live CDN, which CORS-rejects the
  // localhost origin. Route those fetches to the design-tools dev bucket
  // instead so previews work end-to-end during local development. Every
  // other origin (preview links, staging, production) uses the configured
  // mediaBaseUrl as before.
  var SVG_BASE = window.location.origin === 'http://127.0.0.1:9292'
    ? 'https://design-tools-storage-dev.s3.us-east-1.amazonaws.com/PATTERN_SETS/'
    : ((window.APP_CONFIG && window.APP_CONFIG.roots.media) || 'https://media.squadmojo-dev.com') + '/PATTERN_SETS/';
  var cache    = new Map();

  function fetchSvg(key) {
    if (!key) return Promise.resolve(null);
    if (cache.has(key)) return Promise.resolve(cache.get(key));
    return fetch(SVG_BASE + key)
      .then(function (res) { if (!res.ok) throw new Error(); return res.text(); })
      .then(function (t)   { cache.set(key, t); return t; })
      .catch(function ()   { cache.set(key, null); return null; });
  }

  // Returns undefined if never fetched, null if a previous fetch failed, or
  // the cached SVG string. Mirrors Map.get() semantics on purpose so callers
  // can distinguish "not attempted" from "attempted but failed".
  function getCached(key) {
    return cache.get(key);
  }

  // Accepts both naming conventions used historically:
  //   { primary, secondary, tertiary, accent }            (team-text style)
  //   { primaryColor, secondaryColor, tertiaryColor, accentColor }  (swatch style)
  function colorize(svgText, theme) {
    var out       = svgText;
    var primary   = theme.primary   || theme.primaryColor;
    var secondary = theme.secondary || theme.secondaryColor;
    var tertiary  = theme.tertiary  || theme.tertiaryColor;
    var accent    = theme.accent    || theme.accentColor;
    if (primary)   out = out.replace(/#1e88e5/gi, primary);
    if (secondary) out = out.replace(/#43a047/gi, secondary);
    if (tertiary)  out = out.replace(/#fb8c00/gi, tertiary);
    // Some source SVGs paint g#teamText with #e53b37 instead of the #e53935
    // accent sentinel — treat both as accent so the placeholder team name
    // recolors with the swatch.
    if (accent)    out = out.replace(/#e53935|#e53b37/gi, accent);
    return out;
  }

  window.TeamDesignSvg = {
    fetch:     fetchSvg,
    getCached: getCached,
    colorize:  colorize,
  };
})();
